export interface TemplateParts {
  html: string;
  css: string;
  js: string;
}

export type TemplateEditorTab = 'html' | 'css' | 'js';

const STYLE_RE = /<style[^>]*>([\s\S]*?)<\/style>/gi;
const SCRIPT_RE = /<script(?![^>]*\bsrc\b)[^>]*>([\s\S]*?)<\/script>/gi;
const BODY_RE = /<body[^>]*>([\s\S]*?)<\/body>/i;
const HEAD_RE = /<head[^>]*>([\s\S]*?)<\/head>/i;

function extractAll(re: RegExp, source: string): string[] {
  const results: string[] = [];
  const globalRe = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`);
  let match: RegExpExecArray | null;
  while ((match = globalRe.exec(source)) !== null) {
    results.push(match[1].trim());
  }
  return results;
}

function stripInlineAssets(source: string): string {
  return source
    .replace(STYLE_RE, '')
    .replace(SCRIPT_RE, '')
    .trim();
}

/** Split a full HTML template into editable HTML / CSS / JS sections */
export function splitTemplate(htmlTemplate: string): TemplateParts {
  const styles = extractAll(STYLE_RE, htmlTemplate);
  const scripts = extractAll(SCRIPT_RE, htmlTemplate);

  const css = styles.join('\n\n');
  const js = scripts.join('\n\n');

  const bodyMatch = htmlTemplate.match(BODY_RE);
  if (bodyMatch) {
    return {
      html: stripInlineAssets(bodyMatch[1]),
      css,
      js,
    };
  }

  const headMatch = htmlTemplate.match(HEAD_RE);
  const hasDocumentShell = /<!DOCTYPE/i.test(htmlTemplate) || /<html[\s>]/i.test(htmlTemplate);

  if (hasDocumentShell) {
    let html = htmlTemplate
      .replace(/<!DOCTYPE[^>]*>/i, '')
      .replace(/<\/?html[^>]*>/gi, '')
      .replace(HEAD_RE, '')
      .replace(/<\/?body[^>]*>/gi, '');

    html = stripInlineAssets(html).trim();
    return { html, css, js };
  }

  return {
    html: stripInlineAssets(htmlTemplate),
    css,
    js,
  };
}

/** Combine editable sections back into a single HTML document for preview / storage */
export function combineTemplateParts(parts: TemplateParts): string {
  const html = parts.html.trim();
  const css = parts.css.trim();
  const js = parts.js.trim();

  const hasFullDocument =
    /<!DOCTYPE/i.test(html) ||
    (/^<html[\s>]/i.test(html) && /<\/html>\s*$/i.test(html));

  if (hasFullDocument) {
    let combined = html;
    if (css) {
      const styleBlock = `<style>\n${css}\n</style>`;
      if (/<\/head>/i.test(combined)) {
        combined = combined.replace(/<\/head>/i, `${styleBlock}\n</head>`);
      } else if (/<head[^>]*>/i.test(combined)) {
        combined = combined.replace(/<head([^>]*)>/i, `<head$1>\n${styleBlock}`);
      } else {
        combined = combined.replace(/<html([^>]*)>/i, `<html$1>\n<head>\n${styleBlock}\n</head>`);
      }
    }
    if (js) {
      const scriptBlock = `<script>\n${js}\n</script>`;
      if (/<\/body>/i.test(combined)) {
        combined = combined.replace(/<\/body>/i, `${scriptBlock}\n</body>`);
      } else {
        combined = `${combined}\n${scriptBlock}`;
      }
    }
    return combined;
  }

  const cssBlock = css ? `<style>\n${css}\n</style>\n` : '';
  const jsBlock = js ? `\n<script>\n${js}\n</script>` : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${cssBlock}</head>
<body>
${html}
${jsBlock}
</body>
</html>`;
}

export function getPartLineCount(part: string): number {
  if (!part) return 1;
  return part.split('\n').length;
}

const DEFAULT_HTML_DOCUMENT = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: system-ui, sans-serif; padding: 24px; color: #1e293b; font-size: 13px; }
    h1 { color: #4f46e5; margin-bottom: 6px; font-size: 20px; }
    .subtitle { color: #64748b; margin-bottom: 16px; font-size: 12px; }
    .metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin: 16px 0; }
    .metric-card {
      background: linear-gradient(135deg, #eef2ff, #f5f3ff);
      border-radius: 8px;
      padding: 14px;
      border: 1px solid #e0e7ff;
    }
    .metric-label { font-size: 10px; color: #6366f1; text-transform: uppercase; letter-spacing: 0.05em; }
    .metric-value { font-size: 22px; font-weight: 700; color: #312e81; margin-top: 2px; }
    .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; color: #94a3b8; font-size: 11px; }
  </style>
</head>
<body>
  <h1>{conversationStats.title}</h1>
  <p class="subtitle">Reporting period: {conversationStats.period}</p>
  <div class="metrics">
    <div class="metric-card">
      <div class="metric-label">Total Sessions</div>
      <div class="metric-value">{conversationStats.metrics.totalSessions}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Avg Duration</div>
      <div class="metric-value">{conversationStats.metrics.avgDuration}</div>
    </div>
    <div class="metric-card">
      <div class="metric-label">Satisfaction</div>
      <div class="metric-value">{conversationStats.metrics.satisfaction} ★</div>
    </div>
  </div>
  <div class="footer">Generated for agent: {agentInfo.name} · {agentInfo.environment}</div>
</body>
</html>`;

export const DEFAULT_TEMPLATE_PARTS: TemplateParts = splitTemplate(DEFAULT_HTML_DOCUMENT);
